import { createContext, use, useEffect, useRef, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { isStaleChunkError } from '../lib/stale-chunk';
import { RouteErrorFallback } from './RouteErrorFallback';
import { StaleChunkRecovery } from './StaleChunkRecovery';

interface Props {
  children: ReactNode;
  /** A crashed boundary recovers when this changes from what it was at the crash, without remounting its children. */
  resetKey?: string;
}

const ResetKeyContext = createContext<string | undefined>(undefined);

/**
 * Sentry renders `fallback` as a component type, so it must keep one identity
 * across renders; the reset key reaches it through context instead of a closure.
 */
function RouteFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  const resetKey = use(ResetKeyContext);
  const keyAtCrashRef = useRef(resetKey);
  const changed = resetKey !== keyAtCrashRef.current;
  useEffect(() => {
    if (changed) resetError();
  }, [changed, resetError]);
  return isStaleChunkError(error) ? <StaleChunkRecovery /> : <RouteErrorFallback error={error} onReset={resetError} />;
}

/**
 * Per-route error boundary that isolates crashes to the current route.
 * Wrapping individual routes means a crash in chat won't take down invoicing -
 * the sidebar remains functional and users can navigate away.
 *
 * Uses Sentry.ErrorBoundary so crashes are reported with a 'boundary: route' tag,
 * distinguishing them from the root ErrorBoundary ('boundary: root').
 *
 * A stale chunk (a deploy invalidated the chunk URL) renders `StaleChunkRecovery`,
 * which reloads onto the new deploy instead of showing an error.
 */
export function RouteErrorBoundary({ children, resetKey }: Props) {
  return (
    <ResetKeyContext value={resetKey}>
      <Sentry.ErrorBoundary beforeCapture={(scope) => scope.setTag('boundary', 'route')} fallback={RouteFallback}>
        {children}
      </Sentry.ErrorBoundary>
    </ResetKeyContext>
  );
}
